'use client';

import { ArrowRight } from 'lucide-react';
import { applyTextStyle } from '@/lib/textStyle';

/**
 * CtaBlock — bandeau d'appel à l'action.
 * Variants : centered | split.
 * Backgrounds : preset (primary | gradient | dark) OU couleur hex personnalisée
 *               via config.background_color (priorité sur preset si renseigné).
 *
 * Les champs textuels (title, subtitle, button_text, secondary_button_text)
 * acceptent chacun un `<champ>_style` (taille, B/I/U, couleur, alignement).
 */
export default function CtaBlock({ config }) {
  const variant = config.variant || 'centered';
  const bg = config.background || 'primary';

  const presets = {
    primary: 'var(--color-primary)',
    gradient: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)',
    dark: '#1a1a1a',
  };

  // Couleur hex personnalisée prioritaire si fournie. Sinon fallback preset.
  const customBg = config.background_color || '';
  const finalBg = customBg || presets[bg] || presets.primary;
  // Détecte si le fond est sombre pour ajuster les couleurs des boutons.
  const isDarkBg = bg === 'dark' || (customBg && isHexDark(customBg));

  const sectionStyle = {
    padding: '40px 0',
    background: finalBg,
    color: isDarkBg ? '#fff' : 'var(--color-primary-text)',
  };

  // Styles texte par-champ, fusionnés avec les valeurs par défaut de chaque variant.
  const titleBaseCentered  = { fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, lineHeight: 1.15, margin: '0 0 16px', letterSpacing: '-0.02em' };
  const titleBaseSplit     = { fontSize: 'clamp(24px, 3vw, 32px)', fontWeight: 800, lineHeight: 1.15, margin: '0 0 8px', letterSpacing: '-0.02em' };
  const subtitleBaseC      = { fontSize: 'clamp(16px, 1.8vw, 18px)', lineHeight: 1.6, opacity: 0.92, margin: '0 0 32px' };
  const subtitleBaseS      = { fontSize: '16px', lineHeight: 1.6, opacity: 0.92, margin: 0 };

  if (variant === 'split') {
    return (
      <section style={sectionStyle}>
        <div className="container-page" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '40px',
          flexWrap: 'wrap',
        }}>
          <div style={{ flex: '1 1 400px' }}>
            <h2 style={applyTextStyle(config.title_style, titleBaseSplit)}>
              {config.title}
            </h2>
            {config.subtitle && (
              <p style={applyTextStyle(config.subtitle_style, subtitleBaseS)}>
                {config.subtitle}
              </p>
            )}
          </div>
          <Buttons config={config} isDarkBg={isDarkBg} />
        </div>
      </section>
    );
  }

  // centered
  return (
    <section style={{ ...sectionStyle, textAlign: 'center' }}>
      <div className="container-page" style={{ maxWidth: '760px' }}>
        <h2 style={applyTextStyle(config.title_style, titleBaseCentered)}>
          {config.title}
        </h2>
        {config.subtitle && (
          <p style={applyTextStyle(config.subtitle_style, subtitleBaseC)}>
            {config.subtitle}
          </p>
        )}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Buttons config={config} isDarkBg={isDarkBg} />
        </div>
      </div>
    </section>
  );
}

function Buttons({ config, isDarkBg }) {
  const baseBtnPrimary = {
    display: 'inline-flex', alignItems: 'center', gap: '8px',
    padding: '14px 28px',
    background: '#fff',
    color: isDarkBg ? '#1a1a1a' : 'var(--color-primary)',
    border: 'none', borderRadius: '12px',
    fontSize: '16px', fontWeight: 700,
    textDecoration: 'none',
    transition: 'transform 0.15s, box-shadow 0.15s',
  };
  const baseBtnSecondary = {
    display: 'inline-flex', alignItems: 'center', gap: '8px',
    padding: '12px 26px',
    background: 'transparent',
    color: '#fff',
    border: '2px solid rgba(255,255,255,0.5)',
    borderRadius: '12px',
    fontSize: '16px', fontWeight: 600,
    textDecoration: 'none',
  };
  return (
    <>
      {config.button_text && (
        <a
          href={config.button_url || '#'}
          style={applyTextStyle(config.button_text_style, baseBtnPrimary)}
        >
          {config.button_text} <ArrowRight size={16} />
        </a>
      )}
      {config.secondary_button_text && (
        <a
          href={config.secondary_button_url || '#'}
          style={applyTextStyle(config.secondary_button_text_style, baseBtnSecondary)}
        >
          {config.secondary_button_text}
        </a>
      )}
    </>
  );
}

// Détecte si une couleur hex est "sombre" (luminance perçue < 0.5).
// Sert à choisir si les boutons "primary" doivent être blancs ou foncés.
function isHexDark(hex) {
  if (!hex || typeof hex !== 'string') return false;
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // Luminance relative WCAG simplifiée
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum < 0.5;
}
